import { getClientId, clearClientId } from "../utils/clientId";
import { STORAGE_KEYS } from "@config/app.config";
import { getLocalStorage } from "@common";
import {
  getWebSocketConfig,
  configureAutoReconnect,
  configureHeartbeat,
  type AutoReconnectConfig,
  type HeartbeatConfig,
} from "../utils/websocketConfig";
import { getEnvironmentConfig } from "../adapters/environment";
import { store } from "../stores/redux/store";
import { addMessage as addMessageToRedux } from "../features/messages/messagesSlice";
import { useChatStore } from "../stores/chatStore";
import type { Message } from "../interfaces/chat.interface";

export type ConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "failed";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MessageHandler = (messageData: any) => void;
type ConnectionStateHandler = (state: ConnectionState) => void;

export class WebSocketService {
  private websocket: WebSocket | null = null;
  private connectionPromise: Promise<boolean> | null = null;
  private messageListeners: Set<MessageHandler> = new Set();
  private connectionStateHandlers: Set<ConnectionStateHandler> = new Set();
  private currentToken: string | null = null;
  private isManualDisconnect = false;

  private enableClientFiltering: boolean = true;

  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionState: ConnectionState = "disconnected";

  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private lastPongReceived: number = 0;

  private connectionStartTime: number = 0;
  private lastConnectionTime: number = 0;
  private connectionDuration: number = 0;
  private totalReconnectTime: number = 0;
  private wsUrl: string | null = null;
  private customSocketUrl: string | null = null;

  configureClientFiltering(enabled: boolean): void {
    this.enableClientFiltering = enabled;
  }

  /**
   * Set custom socket URL to override environment-based config
   * This allows passing socket URL from parent application
   */
  setSocketUrl(socketUrl: string | null): void {
    this.customSocketUrl = socketUrl;
  }

  private getReconnectDelay(): number {
    const config = getWebSocketConfig();
    const delay = Math.min(
      config.autoReconnect.initialDelay *
        Math.pow(
          config.autoReconnect.backoffMultiplier,
          this.reconnectAttempts
        ),
      config.autoReconnect.maxDelay
    );
    return delay + Math.random() * 1000;
  }

  private async forceCloseExistingConnection(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.websocket) {
        resolve();
        return;
      }

      const existingWs = this.websocket;

      if (existingWs.readyState === WebSocket.CLOSED) {
        resolve();
        return;
      }

      const closeHandler = () => {
        console.log("🔌 Previous WebSocket connection properly closed");
        resolve();
      };

      if (existingWs.readyState === WebSocket.CLOSING) {
        existingWs.addEventListener("close", closeHandler, { once: true });
        return;
      }

      existingWs.addEventListener("close", closeHandler, { once: true });

      try {
        existingWs.close(1000, "Creating new connection");
      } catch (error) {
        console.warn("Error closing WebSocket:", error);
        resolve();
      }

      setTimeout(() => {
        console.warn("⚠️ WebSocket close timeout, continuing anyway");
        resolve();
      }, 2000);
    });
  }

  /**
   * Kiểm tra xem message có phải dành cho client hiện tại không
   * Nếu client filtering tắt hoặc không có targetClientId, trả về true
   */
  private isMessageForCurrentClient(messageData: unknown): boolean {
    // Nếu tắt filtering, chấp nhận tất cả message
    if (!this.enableClientFiltering) {
      return true;
    }

    // Parse message nếu là string
    let parsedMessage: unknown;
    try {
      parsedMessage =
        typeof messageData === "string" ? JSON.parse(messageData) : messageData;
    } catch {
      // Nếu parse lỗi, chấp nhận message (an toàn hơn)
      return true;
    }

    // Kiểm tra targetClientId
    const currentClientId = getClientId();
    const messageObj = parsedMessage as { targetClientId?: string; clientId?: string };
    const targetClientId = messageObj.targetClientId || messageObj.clientId;

    // Nếu không có targetClientId, chấp nhận message
    if (!targetClientId) {
      return true;
    }

    // Chỉ chấp nhận nếu targetClientId khớp với client hiện tại
    return targetClientId === currentClientId;
  }

  /**
   * Kiểm tra xem data có phải là message format không
   * Message format: có messageId hoặc id, có conversationId, và không có type
   */
  private isMessageFormat(data: unknown): boolean {
    if (!data || typeof data !== "object" || Array.isArray(data) || data === null) {
      return false;
    }

    const obj = data as Record<string, unknown>;
    const hasMessageId = "messageId" in obj || "id" in obj;
    const hasConversationId = "conversationId" in obj;
    const hasNoType = !("type" in obj);

    return hasMessageId && hasConversationId && hasNoType;
  }

  /**
   * Normalize direction: "inbound"/"outbound" → "INBOUND"/"OUTBOUND"
   */
  private normalizeDirection(
    direction?: string
  ): "INBOUND" | "OUTBOUND" | undefined {
    if (!direction) return undefined;
    const upper = direction.toUpperCase();
    return upper === "INBOUND" || upper === "OUTBOUND" ? upper : undefined;
  }

  /**
   * Transform extraData for Zalo messages
   */
  private transformZaloExtraData(
    messageType: string,
    extraData: unknown,
    provider?: string
  ): Partial<Message["meta"]> {
    if (provider !== "zalo" || !extraData || typeof extraData !== "object") {
      return {};
    }

    const extra = extraData as Record<string, unknown>;
    const meta: Partial<Message["meta"]> = {};

    switch (messageType) {
      case "TEMPLATE": {
        // Get templateUrl directly from extraData.url
        if (extra.url && typeof extra.url === "string") {
          meta.templateUrl = extra.url;
          meta.originalMessageType = "TEMPLATE";
        }
        break;
      }

      case "LIST": {
        // Get data directly from extraData.*
        const title = (extra.title as string) || "";
        const thumbnail = (extra.thumbnail as string) || "";
        const description = (extra.description as string) || "";
        const url = (extra.url as string) || "";
        const type = (extra.type as string) || "link";

        meta.attachmentJson = JSON.stringify({
          text: title,
          attachments: [
            {
              type: type,
              payload: {
                thumbnail: thumbnail,
                description: description,
                title: title,
                url: url,
              },
            },
          ],
        });
        meta.originalMessageType = "LIST";
        break;
      }

      case "CALL_REQUEST": {
        const callDuration = extra.call_duration;
        if (callDuration !== undefined) {
          meta.callDuration =
            typeof callDuration === "string"
              ? parseInt(callDuration, 10)
              : typeof callDuration === "number"
                ? callDuration
                : 0;
        }
        meta.originalMessageType = "CALL_REQUEST";
        break;
      }

      case "STICKER": {
        // Get sticker URL from extraData.url or content
        if (extra.url && typeof extra.url === "string") {
          meta.previewUrl = extra.url;
        }
        meta.originalMessageType = "STICKER";
        break;
      }

      case "IMAGE":
      case "VIDEO":
      case "AUDIO":
        if (extra.url || extra.thumbnail) {
          meta.attachments = [
            {
              type: messageType.toLowerCase(),
              payload: {
                url: extra.url || "",
                thumbnail: extra.thumbnail || "",
              },
              url: extra.url || "",
            },
          ];
        }
        break;
    }

    return meta;
  }

  /**
   * Transform raw message data thành Message object chuẩn
   */
  private transformRawMessageToMessage(rawData: {
    messageId?: string;
    id?: string;
    conversationId: string;
    timestamp?: number;
    messageType?: string;
    direction?: "INBOUND" | "OUTBOUND" | "inbound" | "outbound";
    content?: string;
    userId?: string;
    provider?: string;
    actorSource?: string;
    showAgentSeparator?: boolean;
    extraData?: unknown;
    [key: string]: unknown;
  }): Message {
    // Map messageType từ server sang type trong Message interface
    const messageTypeMap: Record<string, Message["type"]> = {
      TEXT: "text",
      IMAGE: "image",
      VIDEO: "video",
      FILE: "file",
      LINK: "link",
      AUDIO: "audio",
      TEMPLATE: "template",
      LIST: "link",
      CALL_REQUEST: "call_request",
      STICKER: "image",
    };

    // Lấy ID của message
    const messageId = rawData.messageId || rawData.id || "";

    // Normalize direction (lowercase → uppercase)
    const normalizedDirection = this.normalizeDirection(rawData.direction);

    // Xác định createdAt timestamp
    const createdAt = new Date(rawData.timestamp || Date.now()).toISOString();

    // Xác định type của message
    const type = messageTypeMap[rawData.messageType as string] || "text";

    // Xác định status dựa trên direction
    const status: Message["status"] =
      normalizedDirection === "OUTBOUND" ? "sent" : "read";

    // Transform extraData for Zalo messages
    const extraDataMeta = this.transformZaloExtraData(
      rawData.messageType || "",
      rawData.extraData,
      rawData.provider
    );

    // For STICKER, use URL from extraData.url or content as body
    let body = rawData.content || "";
    if (rawData.messageType === "STICKER") {
      if (
        rawData.extraData &&
        typeof rawData.extraData === "object"
      ) {
        const extra = rawData.extraData as Record<string, unknown>;
        if (extra.url && typeof extra.url === "string") {
          body = extra.url;
        } else if (rawData.content && typeof rawData.content === "string" && rawData.content.startsWith("http")) {
          body = rawData.content;
        }
      } else if (rawData.content && typeof rawData.content === "string" && rawData.content.startsWith("http")) {
        body = rawData.content;
      }
    }

    // Tạo Message object
    const message: Message = {
      id: messageId,
      conversationId: rawData.conversationId,
      sender: {
        id: rawData.userId || "",
        name: "",
        avatar: "",
      },
      body,
      createdAt,
      status,
      type,
      meta: {
        direction: normalizedDirection,
        provider: rawData.provider,
        ...(rawData.actorSource && { actorSource: rawData.actorSource }),
        ...extraDataMeta,
      },
    };

    return message;
  }

  /**
   * Xử lý tin nhắn mới: transform và thêm vào stores
   */
  private handleNewMessage(rawData: {
    messageId?: string;
    id?: string;
    conversationId: string;
    timestamp?: number;
    messageType?: string;
    direction?: "INBOUND" | "OUTBOUND" | "inbound" | "outbound";
    content?: string;
    userId?: string;
    provider?: string;
    actorSource?: string;
    [key: string]: unknown;
  }): void {
    try {
      // Normalize direction trước khi check
      const normalizedDirection = this.normalizeDirection(rawData.direction);
      
      // Skip OUTBOUND TEMPLATE messages (optimistically handled), except Zalo
      if (
        rawData.messageType === "TEMPLATE" &&
        normalizedDirection === "OUTBOUND" &&
        rawData.provider !== "zalo"
      ) {
        return;
      }

      // Transform raw data thành Message object
      const message = this.transformRawMessageToMessage(rawData);

      // Kiểm tra xem conversation này có đang được chọn không
      const chatStoreState = useChatStore.getState();
      const isActiveConversation =
        chatStoreState.selectedConversationId === message.conversationId;

      // Thêm message vào chatStore
      useChatStore.getState().addMessage(
        message.conversationId,
        message,
        isActiveConversation
      );

      // Thêm message vào Redux store
      store.dispatch(
        addMessageToRedux({
          conversationId: message.conversationId,
          message: message,
        })
      );
    } catch (error) {
      console.error("❌ socketService: Error handling new message", error);
    }
  }

  private async connectInternal(userToken: string): Promise<boolean> {
    if (!userToken) {
      throw new Error("Token is required for WebSocket connection");
    }

    if (this.connectionPromise) {
      try {
        await this.connectionPromise;
        if (this.websocket?.readyState === WebSocket.OPEN) {
          return true;
        }
      } catch (error) {
        void error;
        console.log(
          "Previous connection attempt failed, trying new connection"
        );
      }
    }

    this.currentToken = userToken;
    this.connectionStartTime = Date.now();
    this.setConnectionState("connecting");

    const config = getEnvironmentConfig();
    const clientId = getClientId();

    if (this.customSocketUrl) {
      let baseUrl = this.customSocketUrl;
      if (!baseUrl.includes("?") && !baseUrl.endsWith("/")) {
        baseUrl = baseUrl + "/";
      }
      const separator = baseUrl.includes("?") ? "&" : "?";
      this.wsUrl = `${baseUrl}${separator}token=${encodeURIComponent(
        userToken
      )}&clientId=${encodeURIComponent(clientId)}`;
    } else if (config.socketUrl) {
      const separator = config.socketUrl.includes("?") ? "&" : "?";
      this.wsUrl = `${config.socketUrl}${separator}token=${encodeURIComponent(
        userToken
      )}&clientId=${encodeURIComponent(clientId)}`;
    } else if (config.environment === "local") {
      this.wsUrl = `ws://localhost:9061/ws/chat/v2/?token=${encodeURIComponent(
        userToken
      )}&clientId=${encodeURIComponent(clientId)}`;
    } else {
      this.wsUrl = `wss://${config.apiHost}/ws/chat/v2/?token=${encodeURIComponent(
        userToken
      )}&clientId=${encodeURIComponent(clientId)}`;
    }

    await this.forceCloseExistingConnection();

    this.websocket = new WebSocket(this.wsUrl);

    this.connectionPromise = new Promise<boolean>((resolve, reject) => {
      if (!this.websocket) {
        reject(new Error("WebSocket not initialized"));
        return;
      }

      this.websocket.onopen = () => {
        this.lastConnectionTime = Date.now();
        this.connectionDuration =
          this.lastConnectionTime - this.connectionStartTime;

        this.reconnectAttempts = 0;
        this.totalReconnectTime = 0;
        this.setConnectionState("connected");
        this.startHeartbeat();
        resolve(true);
      };

      this.websocket.onerror = (event) => {
        console.error("❌ WebSocket error:", {
          type: event.type,
          target: event.target,
          url: this.wsUrl,
          readyState: this.websocket?.readyState,
          timestamp: new Date().toISOString(),
        });
        reject(event);
      };

      this.websocket.onclose = (event) => {
        this.stopHeartbeat();

        // Log close event details
        console.log(`🔌 WebSocket closed:
        Code: ${event.code}
        Reason: ${event.reason || "No reason provided"}
        Was Clean: ${event.wasClean}
        URL: ${this.wsUrl}
        Timestamp: ${new Date().toISOString()}`);

        this.websocket = null;

        if (!this.isManualDisconnect) {
          if (this.connectionPromise) {
            reject(
              new Error(`WebSocket closed: ${event.reason || "Unknown reason"}`)
            );
          }
          this.setConnectionState("disconnected");

          const closeCode = event.code;
          const isAuthError =
            closeCode === 1008 || (closeCode >= 4001 && closeCode <= 4003);

          if (isAuthError) {
            console.error(
              "❌ WebSocket authentication failed, stopping reconnect attempts"
            );
            this.setConnectionState("failed");
            return;
          }

          this.attemptReconnect();
        }
      };

      this.websocket.onmessage = (event) => {
        try {
          // Parse JSON từ raw event data
          const data = JSON.parse(event.data);

          // Xử lý pong response (phản hồi từ server cho ping)
          if (data.type === "pong") {
            this.handlePong();
            return;
          }

          // Xử lý ping request (server yêu cầu pong)
          if (data.type === "ping") {
            if (
              this.websocket &&
              this.websocket.readyState === WebSocket.OPEN
            ) {
              this.websocket.send(
                JSON.stringify({
                  type: "pong",
                  timestamp: Date.now(),
                  clientId: getClientId(),
                })
              );
            }
            return;
          }

          // Broadcast CONVERSATION_STATE_CHANGED to all listeners, before client filter
          if (
            data &&
            typeof data === "object" &&
            !Array.isArray(data) &&
            data !== null
          ) {
            const dataObj = data as Record<string, unknown>;
            const actualPayload =
              dataObj.data && typeof dataObj.data === "object" && !Array.isArray(dataObj.data)
                ? (dataObj.data as Record<string, unknown>)
                : dataObj;

            if (actualPayload.messageType === "CONVERSATION_STATE_CHANGED") {
              if (this.messageListeners.size > 0) {
                this.messageListeners.forEach((listener) => {
                  try {
                    listener(actualPayload);
                  } catch (listenerError) {
                    console.error(
                      "❌ socketService: Error in message listener for CONVERSATION_STATE_CHANGED",
                      listenerError
                    );
                  }
                });
              }
              return;
            }
          }

          // Kiểm tra xem message có phải dành cho client hiện tại không
          if (!this.isMessageForCurrentClient(event.data)) {
            console.log(
              "🔍 socketService: Message filtered out (not for current client)",
              {
                data: event.data,
                clientId: getClientId(),
                timestamp: new Date().toISOString(),
              }
            );
            return;
          }

          // Xử lý tin nhắn mới (format: có messageId/id, conversationId, không có type)
          if (this.isMessageFormat(data)) {
            this.handleNewMessage(data);
            return;
          }

          console.log("🟡 socketService: Forwarding message to listeners", {
            data,
            isMessageFormat: this.isMessageFormat(data),
            listenersCount: this.messageListeners.size,
          });

          this.messageListeners.forEach((listener) => {
            try {
              listener(data);
            } catch (listenerError) {
              console.error(
                "❌ socketService: Error in message listener",
                listenerError
              );
            }
          });
        } catch {
          // Nếu parse JSON thất bại, vẫn thử gọi listeners với raw data
          // (phòng trường hợp listener muốn xử lý non-JSON data)
          if (this.isMessageForCurrentClient(event.data)) {
            this.messageListeners.forEach((listener) => {
              try {
                listener(event.data);
              } catch (listenerError) {
                console.error(
                  "❌ socketService: Error in message listener (raw data)",
                  listenerError
                );
              }
            });
          }
        }
      };
    });

    return this.connectionPromise;
  }

  async connect(token?: string): Promise<void> {
    if (
      this.websocket &&
      this.websocket.readyState === WebSocket.OPEN &&
      this.currentToken === token
    ) {
      console.log("📡 Already connected with same token, reusing connection");
      return;
    }

    if (this.connectionPromise && this.currentToken === token) {
      console.log(
        "📡 Connection in progress with same token, waiting for existing attempt"
      );
      try {
        await this.connectionPromise;
        return;
      } catch (error) {
        void error;
      }
    }

    const authToken =
      token || this.currentToken || getLocalStorage(STORAGE_KEYS.AUTH_TOKEN);

    if (!authToken) {
      console.error("SocketService: No token provided");
      this.setConnectionState("failed");
      throw new Error("Token is required for WebSocket connection");
    }

    // console.log("📡 Starting new connection attempt...");
    this.isManualDisconnect = false;
    this.stopReconnectTimer();
    this.reconnectAttempts = 0;
    this.totalReconnectTime = 0;

    try {
      await this.connectInternal(authToken);
    } catch (error) {
      void error;
      const config = getWebSocketConfig();
      if (config.autoReconnect.enabled && !this.isManualDisconnect) {
        this.attemptReconnect();
      }
      throw error;
    }
  }

  private stopReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private attemptReconnect(): void {
    const config = getWebSocketConfig();

    if (
      !config.autoReconnect.enabled ||
      this.isManualDisconnect ||
      !this.currentToken
    ) {
      return;
    }

    if (this.reconnectAttempts >= config.autoReconnect.maxRetries) {
      console.error(
        `❌ Max reconnect attempts (${config.autoReconnect.maxRetries}) reached`
      );
      this.setConnectionState("failed");
      return;
    }

    this.reconnectAttempts++;
    const delay = this.getReconnectDelay();
    this.totalReconnectTime += delay;

    console.log(`🔄 Attempting reconnect ${this.reconnectAttempts}/${
      config.autoReconnect.maxRetries
    }:
    ⏰ Delay: ${Math.round(delay)}ms (${(delay / 1000).toFixed(1)}s)
    📊 Total Reconnect Time: ${this.totalReconnectTime}ms (${(
      this.totalReconnectTime / 1000
    ).toFixed(1)}s)
    📍 Last Connected: ${
      this.lastConnectionTime
        ? new Date(this.lastConnectionTime).toLocaleTimeString()
        : "Never"
    }
    ⚙️  Strategy: Exponential backoff (${
      config.autoReconnect.backoffMultiplier
    }x multiplier)
    🎯 Max Delay: ${config.autoReconnect.maxDelay}ms (${
      config.autoReconnect.maxDelay / 1000
    }s)`);

    this.setConnectionState("reconnecting");

    this.reconnectTimer = setTimeout(() => {
      this.connectInternal(this.currentToken!).catch((error) => {
        console.error(
          `❌ Reconnect attempt ${this.reconnectAttempts} failed:`,
          error
        );
      });
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    const config = getWebSocketConfig();
    if (!config.heartbeat.enabled || !this.websocket) {
      return;
    }

    this.lastPongReceived = Date.now();

    this.heartbeatTimer = setInterval(() => {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(
          JSON.stringify({
            type: "ping",
            timestamp: Date.now(),
            clientId: getClientId(),
          })
        );

        this.heartbeatTimeoutTimer = setTimeout(() => {
          console.warn(
            "⚠️ WebSocket heartbeat timeout - connection may be dead"
          );
          this.handleConnectionDead();
        }, config.heartbeat.timeout);
      }
    }, config.heartbeat.interval);
  }

  private handlePong(): void {
    this.lastPongReceived = Date.now();
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }

  private handleConnectionDead(): void {
    console.warn("💀 WebSocket connection appears dead, forcing reconnect");
    if (this.websocket) {
      this.websocket.close();
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }

  disconnect(clearClient: boolean = false): void {
    this.isManualDisconnect = true;
    this.stopHeartbeat();
    this.stopReconnectTimer();

    if (this.websocket) {
      this.websocket.close(1000, "Manual disconnect");
      this.websocket = null;
    }

    this.connectionPromise = null;
    this.reconnectAttempts = 0;
    this.totalReconnectTime = 0;
    this.setConnectionState("disconnected");

    if (clearClient) {
      clearClientId();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  send(data: any): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      const messageWithClientId = {
        ...data,
        clientId: getClientId(),
      };

      this.websocket.send(JSON.stringify(messageWithClientId));
    } else {
      console.warn("WebSocket not connected. Cannot send message.");

      const config = getWebSocketConfig();
      if (this.currentToken && config.autoReconnect.enabled) {
        this.connect(this.currentToken)
          .then(() => {
            if (
              this.websocket &&
              this.websocket.readyState === WebSocket.OPEN
            ) {
              const messageWithClientId = {
                ...data,
                clientId: getClientId(),
              };
              this.websocket.send(JSON.stringify(messageWithClientId));
            }
          })
          .catch((error) => {
            console.error("❌ Failed to reconnect and send message:", error);
          });
      }
    }
  }

  subscribe(handler: MessageHandler): () => void {
    console.log("🟢 socketService: Subscribing new listener", {
      listenersCountBefore: this.messageListeners.size,
      handlerType: typeof handler,
    });
    this.messageListeners.add(handler);
    console.log("🟢 socketService: Listener subscribed", {
      listenersCountAfter: this.messageListeners.size,
    });
    // console.log(
    //   `📝 Added message listener, total: ${this.messageListeners.size}`
    // );
    return () => {
      this.messageListeners.delete(handler);
    };
  }

  unsubscribe(handler: MessageHandler): void {
    this.messageListeners.delete(handler);
  }

  isConnected(): boolean {
    return (
      this.websocket !== null && this.websocket.readyState === WebSocket.OPEN
    );
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.notifyConnectionStateHandlers(state);
    }
  }

  onConnectionStateChange(handler: ConnectionStateHandler): () => void {
    this.connectionStateHandlers.add(handler);
    return () => {
      this.connectionStateHandlers.delete(handler);
    };
  }

  private notifyConnectionStateHandlers(state: ConnectionState): void {
    this.connectionStateHandlers.forEach((handler) => {
      try {
        handler(state);
      } catch (error) {
        console.error(
          "SocketService: Error in connection state handler",
          error
        );
      }
    });
  }

  configureAutoReconnect(config: Partial<AutoReconnectConfig>): void {
    configureAutoReconnect(config);
  }

  configureHeartbeat(config: Partial<HeartbeatConfig>): void {
    configureHeartbeat(config);
  }

  getWebSocket(): WebSocket | null {
    return this.websocket;
  }

  /**
   * Force reconnect (reset counter and reconnect immediately)
   */
  forceReconnect(): void {
    if (!this.currentToken) {
      console.warn("⚠️ No token available for reconnect");
      return;
    }

    console.log("🔄 Force reconnecting...");
    this.reconnectAttempts = 0;
    this.stopReconnectTimer();
    this.totalReconnectTime = 0;

    // Clear any pending connection promise
    this.connectionPromise = null;

    if (this.websocket) {
      this.websocket.close();
    }

    this.connect(this.currentToken).catch((error) => {
      console.error("❌ Force reconnect failed:", error);
    });
  }

  /**
   * Reset service state - useful for debugging
   */
  resetState(): void {
    console.log("🔄 Resetting WebSocket service state");
    this.disconnect();
    this.connectionPromise = null;
    this.reconnectAttempts = 0;
    this.totalReconnectTime = 0;
    this.messageListeners.clear();
    this.connectionStateHandlers.clear();
    this.setConnectionState("disconnected");
  }

  /**
   * Get debug info about current service state
   */
  getDebugInfo(): Record<string, unknown> {
    return {
      connectionState: this.connectionState,
      isConnected: this.isConnected(),
      reconnectAttempts: this.reconnectAttempts,
      hasToken: !!this.currentToken,
      listenersCount: this.messageListeners.size,
      stateListenersCount: this.connectionStateHandlers.size,
      websocketState: this.websocket ? this.websocket.readyState : "null",
      hasConnectionPromise: !!this.connectionPromise,
      clientId: getClientId(),
      clientFilteringEnabled: this.enableClientFiltering,
      connectionStartTime: this.connectionStartTime,
      lastConnectionTime: this.lastConnectionTime,
      connectionDuration: this.connectionDuration,
      totalReconnectTime: this.totalReconnectTime,
      wsUrl: this.wsUrl,
      heartbeatEnabled: getWebSocketConfig().heartbeat.enabled,
      heartbeatInterval: getWebSocketConfig().heartbeat.interval,
      heartbeatTimeout: getWebSocketConfig().heartbeat.timeout,
      lastPongReceived: this.lastPongReceived,
      autoReconnectConfig: getWebSocketConfig().autoReconnect,
    };
  }
}

export const socketService = new WebSocketService();

import { initializeWebSocketConfig } from "../utils/websocketConfig";
initializeWebSocketConfig();
